import {
  Breadcrumb,
  Button,
  Col,
  FormControl,
  FormGroup,
  InputGroup,
  Modal,
  Placeholder,
  Row,
  Table,
} from "react-bootstrap";
import { AccountBreadcrumbs, Copy, WithAccount } from "../util.js";
import {
  Check,
  Clipboard,
  ClipboardCheckFill,
  PencilFill,
  ShieldLock,
  ShieldLockFill,
  Trash,
} from "react-bootstrap-icons";
import { Suspense, useCallback, useEffect, useState } from "react";
import {
  Await,
  Form,
  useActionData,
  useFetcher,
  useLoaderData,
  useNavigation,
} from "react-router";
import { ApiToken } from "../ApiClient.js";
import React from "react";
import { DateTime } from "luxon";

export default function ApiTokens() {
  const navigation = useNavigation();
  return (
    <>
      <Breadcrumbs />
      <Row>
        <Col>
          <h1>
            <ShieldLock />{" "}
            <Suspense fallback={<Placeholder animation="glow" xs={6} />}>
              <WithAccount>{(account) => account.name}</WithAccount>
            </Suspense>{" "}
            API Tokens
          </h1>
        </Col>
      </Row>
      <Row className="mb-3">
        <Col>
          <Form method="POST">
            <Button
              variant="primary"
              type="submit"
              disabled={navigation.state === "submitting"}
            >
              <ShieldLockFill /> Create Token
            </Button>
          </Form>
        </Col>
      </Row>
      <Row>
        <Col>
          <ApiTokenList />
        </Col>
      </Row>
    </>
  );
}

function Breadcrumbs() {
  return (
    <AccountBreadcrumbs>
      <Breadcrumb.Item active>API Tokens</Breadcrumb.Item>
    </AccountBreadcrumbs>
  );
}

function ApiTokenList() {
  const { apiTokens } = useLoaderData() as {
    apiTokens: Promise<ApiToken[]>;
  };

  return (
    <Table>
      <thead>
        <tr>
          <td>Token Name</td>
          <td></td>
          <td>Last Used</td>
          <td>Created</td>
          <td></td>
        </tr>
      </thead>
      <tbody>
        <Suspense>
          <Await resolve={apiTokens}>
            {(apiTokens: ApiToken[]) =>
              apiTokens.map((apiToken) => (
                <ApiTokenRow key={apiToken.id} apiToken={apiToken} />
              ))
            }
          </Await>
        </Suspense>
      </tbody>
    </Table>
  );
}

function TokenName({ apiToken }: { apiToken: ApiToken }) {
  const [isEditing, setIsEditing] = useState(false);
  const edit = useCallback(() => setIsEditing(true), [setIsEditing]);
  const fetcher = useFetcher();
  useEffect(() => {
    // eslint-disable-next-line @eslint-react/set-state-in-effect
    if (fetcher.data) setIsEditing(false);
  }, [fetcher, setIsEditing]);
  if (isEditing) {
    return (
      <fetcher.Form action={apiToken.id} method="PATCH">
        <FormGroup>
          <InputGroup>
            <FormControl
              type="text"
              name="name"
              defaultValue={apiToken.name}
              data-1p-ignore
              autoFocus
            />
            <Button type="submit">
              <Check />
            </Button>
          </InputGroup>
        </FormGroup>
      </fetcher.Form>
    );
  } else {
    return (
      <span onClick={edit}>
        {apiToken.name || `Token ${apiToken.token_hash.slice(0, 5)}`}{" "}
        <Button
          variant="outline-secondary"
          onClick={edit}
          size="sm"
          className="ml-auto"
        >
          <PencilFill />
        </Button>
      </span>
    );
  }
}

function RelativeTime({ time, missing }: { time?: string; missing?: string }) {
  return time ? (
    <relative-time datetime={time} format="relative">
      {DateTime.fromISO(time).toLocal().toLocaleString(DateTime.DATETIME_SHORT)}
    </relative-time>
  ) : (
    <>{missing || "never"}</>
  );
}

function DeleteButton({ apiToken }: { apiToken: ApiToken }) {
  const navigation = useNavigation();

  const [show, setShow] = useState(false);
  const close = React.useCallback(() => {
    // eslint-disable-next-line @eslint-react/set-state-in-effect
    setShow(false);
  }, []);
  const open = React.useCallback(() => setShow(true), []);
  const fetcher = useFetcher();

  useEffect(() => {
    if (fetcher.data) close();
  }, [fetcher, close]);

  return (
    <>
      <Button
        variant="outline-danger"
        className="ml-auto"
        size="sm"
        onClick={open}
      >
        <Trash />
      </Button>
      <Modal show={show} onHide={close}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Token Deletion {apiToken.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          This token will immediately be revoked.{" "}
          {apiToken.last_used_at ? (
            <>
              It was last used <RelativeTime time={apiToken.last_used_at} />
            </>
          ) : (
            <>It has never been used</>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={close}>
            Close
          </Button>
          <fetcher.Form method="DELETE" action={apiToken.id}>
            <Button
              variant="danger"
              type="submit"
              disabled={navigation.state === "submitting"}
            >
              <Trash /> Delete
            </Button>
          </fetcher.Form>
        </Modal.Footer>
      </Modal>
    </>
  );
}

function Token({ token }: { token: string | null }) {
  if (!token) return null;

  return (
    <Copy clipboardContents={token}>
      {(copy, copied) => (
        <span onClick={copy} style={{ cursor: "pointer" }}>
          <code className="user-select-all">{token}</code>{" "}
          <Button size="sm" variant="outline-secondary" className="ml-auto">
            {copied ? <ClipboardCheckFill /> : <Clipboard />}
          </Button>
        </span>
      )}
    </Copy>
  );
}

function ApiTokenRow({ apiToken }: { apiToken: ApiToken }) {
  const actionData = useActionData() as
    undefined | (ApiToken & { token: string });
  const token = actionData?.id == apiToken.id ? actionData?.token : null;

  return (
    <tr className={token ? "table-success" : ""}>
      <td>
        <TokenName apiToken={apiToken} />
      </td>
      <td>
        <Token token={token} />
      </td>
      <td>
        <RelativeTime time={apiToken.last_used_at} />
      </td>
      <td>
        <RelativeTime time={apiToken.created_at} />
      </td>
      <td>
        <DeleteButton apiToken={apiToken} />
      </td>
    </tr>
  );
}
